/**
 * Redirección a colivings externos.
 * - Outsite: directo a https://www.outsite.co/?rfsn=… (sin pasar por /api/out)
 * - Resto: vía /api/out (UTMs + log)
 */

import {
  isOutsiteAffiliateSource,
  OUTSITE_AFFILIATE_FALLBACK,
} from './outsiteAffiliate';
import {
  isBluegroundAffiliateSource,
  buildBluegroundAffiliateUrl,
} from './bluegroundAffiliate';
import { openAffiliatePartnerModal } from './affiliatePartnerModal';
import { postOutboundAffiliateNotify } from './affiliateOutboundNotify';
import { resolveSpacestListingMonthlyPriceEur } from './spacestListingPrice';
import { buildAyaPartnerUrl } from './ayaAffiliate';

/** Slug del proveedor scrapeado (outsite, outsitenyc, urbancampus…). */
export function resolveExternalProvider(coliving) {
  return String(
    coliving?.externalSource || coliving?.sourceProvider || coliving?.source || '',
  ).toLowerCase();
}

/** true si el listing es Outsite afiliado. */
export function isOutsiteAffiliateListing(coliving) {
  return !!coliving?.isExternal && isOutsiteAffiliateSource(resolveExternalProvider(coliving));
}

/** true si el listing es Blueground afiliado. */
export function isBluegroundAffiliateListing(coliving) {
  return !!coliving?.isExternal && isBluegroundAffiliateSource(resolveExternalProvider(coliving));
}

/** true si el listing es Spacest partner (API oficial, redirect con UTMs). */
export function isSpacestPartnerListing(coliving) {
  return !!coliving?.isExternal && resolveExternalProvider(coliving) === 'spacest';
}

/** true si el listing es Hello AYA (NYC partner). */
export function isAyaPartnerListing(coliving) {
  return !!coliving?.isExternal && resolveExternalProvider(coliving) === 'aya';
}

/** Partner afiliado con redirect directo (sin modal de lead). */
export function isAffiliatePartnerListing(coliving) {
  return isOutsiteAffiliateListing(coliving)
    || isBluegroundAffiliateListing(coliving)
    || isSpacestPartnerListing(coliving)
    || isAyaPartnerListing(coliving);
}

/** URL de destino (Outsite → genérico; Blueground → ficha con UTM afiliado). */
export function resolveExternalApplyUrl(coliving) {
  if (isOutsiteAffiliateListing(coliving)) {
    return OUTSITE_AFFILIATE_FALLBACK;
  }
  const raw = coliving?.externalApplyUrl || coliving?.sourceUrl || coliving?.url || '';
  if (isBluegroundAffiliateListing(coliving)) {
    return buildBluegroundAffiliateUrl(raw);
  }
  if (isAyaPartnerListing(coliving)) {
    return buildAyaPartnerUrl(raw);
  }
  return raw;
}

/**
 * Abre URL en pestaña nueva sin navegar la pestaña actual.
 * Más fiable que window.open + location.assign (evita doble redirect).
 */
function openInNewTab(url) {
  if (!url) return;
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.target = '_blank';
  anchor.rel = 'noopener noreferrer';
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
}

/** Log + email admin en background (redirect afiliado: Spacest, Blueground, Outsite). */
function notifyAffiliateClickBackground(coliving, url, { userId, source = 'coliving-search', referrerEmail } = {}) {
  const monthlyPrice = resolveSpacestListingMonthlyPriceEur(coliving);
  postOutboundAffiliateNotify({
    url: url || '',
    provider: resolveExternalProvider(coliving),
    colivingId: coliving?.id || '',
    colivingTitle: coliving?.title || coliving?.propertyTitle || '',
    userId,
    city: coliving?.propertyLocation || coliving?.city || '',
    source,
    action: 'affiliate-redirect',
    referrerEmail,
    priceFrom: monthlyPrice ?? '',
  });
}

/** Abre Outsite directamente — sin /api/out. */
function openOutsiteAffiliateDirect(coliving, options = {}) {
  notifyAffiliateClickBackground(coliving, OUTSITE_AFFILIATE_FALLBACK, options);
  openInNewTab(OUTSITE_AFFILIATE_FALLBACK);
}

/** Abre Blueground directamente (ficha con UTM afiliado) — sin /api/out. */
function openBluegroundAffiliateDirect(coliving, options = {}) {
  const url = resolveExternalApplyUrl(coliving);
  if (!url) return;
  notifyAffiliateClickBackground(coliving, url, options);
  openInNewTab(url);
}

/**
 * Abre un coliving externo vía /api/out (solo proveedores que no son Outsite).
 */
export function openExternalColiving({
  url,
  provider,
  colivingId,
  colivingTitle,
  userId,
  city,
  source = 'coliving-search',
}) {
  if (!url) return;

  const params = new URLSearchParams();
  params.set('url', url);
  if (provider) params.set('provider', provider);
  if (colivingId) params.set('colivingId', colivingId);
  if (colivingTitle) params.set('colivingTitle', colivingTitle);
  if (userId) params.set('userId', userId);
  if (city) params.set('city', city);
  params.set('source', source);

  openInNewTab(`/api/out?${params.toString()}`);
}

/** Abre Spacest directamente — URL ya lleva UTMs de partner (utm_source=coolliving). */
function openSpacestPartnerDirect(coliving, options = {}) {
  const url = resolveExternalApplyUrl(coliving);
  if (!url) return;
  notifyAffiliateClickBackground(coliving, url, options);
  openInNewTab(url);
}

/** Abre Hello AYA en nueva pestaña (UTMs + notify admin). */
function openAyaPartnerDirect(coliving, options = {}) {
  const url = resolveExternalApplyUrl(coliving);
  if (!url) return;
  notifyAffiliateClickBackground(coliving, url, options);
  openInNewTab(url);
}

/** Punto de entrada único para clicks en listings externos. */
export function openExternalColivingListing(coliving, { userId, source = 'coliving-search', referrerEmail } = {}) {
  const notifyOpts = { userId, source, referrerEmail };
  if (isOutsiteAffiliateListing(coliving)) {
    openOutsiteAffiliateDirect(coliving, notifyOpts);
    return;
  }

  if (isBluegroundAffiliateListing(coliving)) {
    openBluegroundAffiliateDirect(coliving, notifyOpts);
    return;
  }

  if (isSpacestPartnerListing(coliving)) {
    openSpacestPartnerDirect(coliving, notifyOpts);
    return;
  }

  if (isAyaPartnerListing(coliving)) {
    openAyaPartnerDirect(coliving, notifyOpts);
    return;
  }

  const url = resolveExternalApplyUrl(coliving);
  if (!url) return;

  openExternalColiving({
    url,
    provider: resolveExternalProvider(coliving),
    colivingId: coliving?.id,
    colivingTitle: coliving?.title || coliving?.propertyTitle || '',
    userId,
    city: coliving?.propertyLocation || coliving?.city || '',
    source,
  });
}

/**
 * Affiliate partner click — modal with email + consent; then redirect (Blueground includes promo code).
 */
export function openAffiliatePartnerListing(coliving, { userId, source = 'coliving-search' } = {}) {
  if (isAffiliatePartnerListing(coliving)) {
    openAffiliatePartnerModal(coliving, { userId, source });
    return;
  }
  openExternalColivingListing(coliving, { userId, source });
}
